import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { Toaster } from 'sonner';

// Pages
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import TasksPage from '@/pages/TasksPage';
import TaskDetailPage from '@/pages/TaskDetailPage';
import NewTaskPage from '@/pages/NewTaskPage';
import QueriesPage from '@/pages/QueriesPage';
import AgentsPage from '@/pages/AgentsPage';
import SettingsPage from '@/pages/SettingsPage';
import MeetingsPage from '@/pages/MeetingsPage';
import MeetingDetailPage from '@/pages/MeetingDetailPage';
import NewMeetingPage from '@/pages/NewMeetingPage';
import RepositorioPage from '@/pages/RepositorioPage';
import HRAgentPage from '@/pages/HRAgentPage';

// Loading component
const LoadingScreen = () => (
  <div className="min-h-screen bg-stone-50 flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900" />
  </div>
);

// Protected route wrapper
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, hasRole } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allowedRoles && !hasRole(allowedRoles)) {
    return <Navigate to="/queries" replace />;
  }

  return children;
};

// Public route wrapper (redirects if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) return <LoadingScreen />;
  if (isAuthenticated) {
    // Redirect based on role
    if (user?.role === 'staff') {
      return <Navigate to="/queries" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={['superadmin', 'direccion']}>
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <TasksPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/tasks/new"
        element={
          <ProtectedRoute allowedRoles={['superadmin', 'direccion']}>
            <NewTaskPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/tasks/:id"
        element={
          <ProtectedRoute>
            <TaskDetailPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/queries"
        element={
          <ProtectedRoute>
            <QueriesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/meetings"
        element={
          <ProtectedRoute allowedRoles={['superadmin', 'ugib', 'departamentos', 'direccion']}>
            <MeetingsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/meetings/new"
        element={
          <ProtectedRoute allowedRoles={['superadmin', 'ugib', 'departamentos', 'direccion']}>
            <NewMeetingPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/meetings/:id"
        element={
          <ProtectedRoute allowedRoles={['superadmin', 'ugib', 'departamentos', 'direccion']}>
            <MeetingDetailPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/repositorio"
        element={
          <ProtectedRoute allowedRoles={['superadmin', 'ugib']}>
            <RepositorioPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/hr-agent"
        element={
          <ProtectedRoute allowedRoles={['superadmin', 'ugib', 'departamentos', 'direccion']}>
            <HRAgentPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/agents"
        element={
          <ProtectedRoute>
            <AgentsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Redirect root to appropriate page */}
      <Route path="/" element={<Navigate to="/login" replace />} />

      {/* Catch all - redirect to login */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          toastOptions={{
            className: 'font-sans',
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
